// "This Week" — the dashboard centerpiece. Mon-Fri action list generated
// from the content plan, the woven social schedule, and recurring rituals
// (thisWeek.js). Checking a content task advances its status and opens
// the time-on-move prompt (handoff 7.5); checking a social task marks it
// published; the weekly-report check writes kpi_snapshots.

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useContent } from '../../../context/ContentContext';
import { useMarketing } from '../MarketingContext';
import { apiFetch } from '../../../utils/apiFetch';
import { buildThisWeek, weekKey } from './thisWeek';
import { Card, Eyebrow, Pill } from '../ui';
import { isInQueue } from '../pillars';
import TimePromptModal from '../content/TimePromptModal';
import SocialPostModal from '../social/SocialPostModal';

const SOURCE_BADGE = {
  blog: 'bg-bullogic-blue/12 text-bullogic-blue',
  youtube: 'bg-rose/13 text-[#d63a54]',
  linkedin: 'bg-deep-teal/12 text-deep-teal',
  email: 'bg-amber/13 text-amber',
  report: 'bg-chip text-navy',
};

const SOURCE_LABEL = { blog: 'Blog', youtube: 'YouTube', linkedin: 'LinkedIn', email: 'Email', report: 'Report' };

function ritualStorageKey(taskKey) {
  return `bcc_mkt_ritual_done:${weekKey()}:${taskKey}`;
}

function readRitualDone(taskKey) {
  try { return localStorage.getItem(ritualStorageKey(taskKey)) === '1'; } catch { return false; }
}

export default function ThisWeekCard() {
  const { entries, saveOne } = useContent();
  const { social, reloadSocial, summary, ac, traffic, funnel, range } = useMarketing();
  const navigate = useNavigate();
  const [ritualTick, setRitualTick] = useState(0);
  const [busyKey, setBusyKey] = useState(null);
  const [timePrompt, setTimePrompt] = useState(null);
  const [openPost, setOpenPost] = useState(null);
  const [err, setErr] = useState(null);

  // Clicking a task's text opens the thing itself: social posts open in
  // place (copy + mark posted), content jumps to its board column,
  // rituals go to their screen.
  function openTask(task) {
    if (task.socialPostId) {
      const post = (social.data?.posts || []).find(p => p.id === task.socialPostId);
      if (post) setOpenPost(post);
      return;
    }
    if (task.contentId) {
      navigate(`/marketing/content?tab=${task.done ? 'performance' : 'board'}`);
      return;
    }
    if (task.key === 'ritual-newsletter') navigate('/marketing/email');
  }

  const week = useMemo(
    () => buildThisWeek(entries, social.data?.posts || []),
    [entries, social.data]
  );

  const queueDepth = useMemo(
    () => entries.filter(e => isInQueue(e.status)).length,
    [entries]
  );
  const queuePill = queueDepth >= 2
    ? { tone: 'good', label: 'Queue healthy' }
    : queueDepth === 1
      ? { tone: 'warn', label: 'Queue thin' }
      : { tone: 'crit', label: 'Queue empty' };

  // The weekly report ritual writes a kpi_snapshots row per KPI that is
  // currently loaded, so trends accumulate without a separate chore.
  // Range-scoped metrics record the window as their dimension.
  async function writeWeeklySnapshot() {
    const snaps = [];
    const acData = ac.data && ac.data.configured !== false ? ac.data : null;
    if (acData?.total != null) snaps.push({ metric: 'subscribers_total', value: acData.total });
    const traf = traffic.data && traffic.data.configured !== false ? traffic.data : null;
    if (traf?.totals?.sessions != null) snaps.push({ metric: 'sessions', value: traf.totals.sessions, dimension: range });
    const fun = funnel.data && funnel.data.configured !== false ? funnel.data : null;
    if (fun?.calls != null) snaps.push({ metric: 'booked_calls', value: fun.calls, dimension: range });
    if (fun?.signups != null) snaps.push({ metric: 'newsletter_signups', value: fun.signups, dimension: range });
    if (summary.data?.wonCount != null) snaps.push({ metric: 'won_count_ytd', value: summary.data.wonCount });
    if (snaps.length === 0) return;
    const res = await apiFetch('/api/data/kpi/snapshot', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ snapshots: snaps }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
  }

  async function handleCheck(task) {
    setErr(null);
    if (task.ritual) {
      const wasDone = readRitualDone(task.key);
      if (task.key === 'ritual-report' && !wasDone) {
        setBusyKey(task.key);
        try {
          await writeWeeklySnapshot();
        } catch (e) {
          setErr(`Report snapshot failed: ${e.message}`);
          setBusyKey(null);
          return;
        }
        setBusyKey(null);
      }
      try {
        localStorage.setItem(ritualStorageKey(task.key), wasDone ? '0' : '1');
      } catch { /* ignore */ }
      setRitualTick(t => t + 1);
      return;
    }
    if (task.socialPostId) {
      if (busyKey) return;
      setBusyKey(task.key);
      try {
        const res = await apiFetch(`/api/data/social/posts/${task.socialPostId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ status: 'published' }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
        await reloadSocial();
      } catch (e) {
        setErr(e.message);
      } finally {
        setBusyKey(null);
      }
      return;
    }
    if (!task.nextStatus || busyKey) return;
    const entry = entries.find(e => e.id === task.contentId);
    if (!entry) return;
    setBusyKey(task.key);
    try {
      const updates = { ...entry, status: task.nextStatus };
      // Publishing without a date on record: stamp today so cadence
      // math and the done-row in this list stay correct.
      if (task.nextStatus === 'published' && !entry.publishDate) {
        updates.publishDate = new Date().toISOString().slice(0, 10);
      }
      await saveOne(updates);
      // Time-on-move (7.5): the stage just completed is the entry's
      // previous status.
      setTimePrompt({ contentId: entry.id, title: entry.title, fromStage: entry.status, toStage: task.nextStatus });
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusyKey(null);
    }
  }

  return (
    <Card lash="blue" className="p-4 flex-[1.15]">
      <div className="flex items-center justify-between mb-1.5">
        <Eyebrow>This week · grab and go</Eyebrow>
        <Pill tone={queuePill.tone}>{queuePill.label}</Pill>
      </div>

      <div>
        {week.days.map(day => (
          <div
            key={day.label}
            className={`grid grid-cols-[74px_1fr] gap-3 px-1 py-2.5 border-b border-line-2 last:border-b-0 ${
              day.isToday ? 'bg-bullogic-blue/5 rounded-[7px]' : ''
            }`}
          >
            <div className={`text-[11px] font-black uppercase tracking-[0.05em] pt-0.5 ${
              day.isToday ? 'text-bullogic-blue' : 'text-ink-3'
            }`}>
              {day.label}
            </div>
            <div>
              {day.tasks.length === 0 && (
                <div className="text-[12px] text-ink-3 italic py-0.5">open</div>
              )}
              {day.tasks.map(task => {
                const done = task.done || (task.ritual && readRitualDone(task.key));
                return (
                  <div key={task.key + ritualTick} className="flex items-center gap-2.5 py-[3px] text-[13px]">
                    <button
                      onClick={() => handleCheck(task)}
                      disabled={busyKey === task.key || (task.done && !task.ritual)}
                      aria-label={done ? 'Done' : `Mark "${task.title}" done`}
                      className={`w-[15px] h-[15px] rounded-[4px] shrink-0 border-[1.6px] grid place-items-center transition-colors ${
                        done
                          ? 'bg-deep-teal border-deep-teal text-white text-[10px] leading-none'
                          : 'border-ink-3 hover:border-deep-teal cursor-pointer bg-white'
                      }`}
                    >
                      {done ? '✓' : ''}
                    </button>
                    <button
                      onClick={() => openTask(task)}
                      className={`text-left hover:underline decoration-bullogic-blue/50 underline-offset-2 ${done ? 'line-through text-ink-3' : 'text-ink'}`}
                      title={task.socialPostId ? 'Open post (copy + mark posted)' : task.contentId ? 'Open on the board' : undefined}
                    >
                      {task.verb} <b className="text-navy">{task.title}</b>
                    </button>
                    <span className={`ml-auto text-[10px] font-black uppercase tracking-[0.03em] px-1.5 py-px rounded-[4px] whitespace-nowrap ${SOURCE_BADGE[task.source]}`}>
                      {SOURCE_LABEL[task.source]}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {err && <div className="text-[11px] text-rose mt-2">Update failed: {err}</div>}
      <div className="mt-2.5 bg-bullogic-blue/5 border border-dashed border-bullogic-blue/40 rounded-[7px] px-3 py-2 text-[12px] text-ink-2">
        Auto-built from the content plan, the social weave, and weekly rituals. Checking a
        task moves the item along (content moves ask for time).
      </div>
      <TimePromptModal prompt={timePrompt} onClose={() => setTimePrompt(null)} />
      <SocialPostModal post={openPost} onClose={() => setOpenPost(null)} />
    </Card>
  );
}
